import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const Sidebar = ({ isCollapsed = false, onToggle }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navigationItems = [
    {
      section: 'Main',
      items: [
        { name: 'Dashboard', path: '/collection-dashboard', icon: 'LayoutDashboard', description: 'Overview & Analytics' },
        { name: 'Advanced Analytics', path: '/advanced-dashboard', icon: 'LineChart', description: 'ROI & Performance' },
        { name: 'Portfolio Performance', path: '/portfolio-performance', icon: 'BarChart3', description: 'Gains, ROI & Trends' },
        { name: 'Auction Floor', path: '/auction-floor', icon: 'Gavel', description: 'Live Auctions & Bidding' },
        { name: 'Auction Dashboard', path: '/seller-auction-dashboard', icon: 'BarChart2', description: 'Seller Auction Hub' },
        { name: 'My Bid Dashboard', path: '/buyer-auction-dashboard', icon: 'ShoppingBag', description: 'Bids, Wins & Analytics' },
        { name: 'Marketplace', path: '/marketplace', icon: 'Store', description: 'Buy & Sell Collectibles' },
        { name: 'Add Collection', path: '/add-collection', icon: 'Plus', description: 'Create new collection' },
        { name: 'Market Analysis', path: '/market-analysis', icon: 'TrendingUp', description: 'Market insights' },
        { name: 'News Feed', path: '/news-feed', icon: 'Newspaper', description: 'Latest updates' },
      ]
    },
    {
      section: 'Account',
      items: [
        { name: 'Profile', path: '/profile-management', icon: 'User', description: 'Manage account' },
        { name: 'Achievements', path: '/achievements', icon: 'Trophy', description: 'Badges & Milestones' },
        { name: 'Ratings & Trust', path: '/profile-management', icon: 'Star', description: 'Reviews & trust badges' },
      ]
    },
    {
      section: 'Admin',
      items: [
        { name: 'Moderation', path: '/admin-moderation', icon: 'Shield', description: 'Flags, disputes & fraud' },
      ]
    }
  ];

  const isActivePath = (path) => location?.pathname === path;

  const toggleSidebar = () => {
    if (onToggle) {
      onToggle();
    } else {
      setIsOpen(!isOpen);
    }
  };

  const sidebarWidth = isCollapsed ? 'w-16' : 'w-64';
  const mobileOpen = isOpen ? 'translate-x-0' : '-translate-x-full';

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
      {/* Sidebar */}
      <aside
        className={`fixed top-16 left-0 bottom-0 z-50 bg-card border-r border-border transition-all duration-300 ease-premium lg:${sidebarWidth} ${mobileOpen} w-72 sm:w-64 lg:translate-x-0`}
      >
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            {!isCollapsed && (
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-primary to-warning rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="Gem" size={16} color="#1A1B23" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-card-foreground">Collections</h2>
                  <p className="text-xs text-muted-foreground">Premium Hub</p>
                </div>
              </div>
            )}
            
            {/* Toggle Button - Desktop */}
            <button
              onClick={toggleSidebar}
              className="hidden lg:flex p-2.5 rounded-lg hover:bg-muted/50 transition-colors duration-200 premium-hover min-w-[44px] min-h-[44px] items-center justify-center"
            >
              <Icon 
                name={isCollapsed ? "ChevronRight" : "ChevronLeft"} 
                size={16} 
                className="text-muted-foreground" 
              />
            </button>

            {/* Close Button - Mobile */}
            <button
              onClick={() => setIsOpen(false)}
              className="lg:hidden p-2.5 rounded-lg hover:bg-muted/50 transition-colors duration-200 min-w-[44px] min-h-[44px] flex items-center justify-center ml-auto"
              aria-label="Close sidebar"
            >
              <Icon name="X" size={18} className="text-muted-foreground" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4 overscroll-contain">
            <div className="space-y-6">
              {navigationItems?.map((section) => (
                <div key={section?.section}>
                  {!isCollapsed && (
                    <div className="px-4 mb-2">
                      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        {section?.section}
                      </h3>
                    </div>
                  )}
                  
                  <div className="space-y-0.5 px-2">
                    {section?.items?.map((item) => (
                      <a
                        key={item?.path}
                        href={item?.path}
                        className={`group flex items-center px-3 py-3 rounded-lg text-sm font-medium transition-all duration-300 premium-hover min-h-[48px] ${
                          isActivePath(item?.path)
                            ? 'bg-primary/10 text-primary border border-primary/20 shadow-gold'
                            : 'text-muted-foreground hover:text-card-foreground hover:bg-muted/50'
                        }`}
                        onClick={() => setIsOpen(false)}
                      >
                        <div className="flex items-center min-w-0 flex-1">
                          <Icon 
                            name={item?.icon} 
                            size={20} 
                            className={`flex-shrink-0 ${
                              isActivePath(item?.path) ? 'text-primary' : 'text-muted-foreground group-hover:text-card-foreground'
                            }`}
                          />
                          
                          {!isCollapsed && (
                            <div className="ml-3 min-w-0 flex-1">
                              <div className="truncate">{item?.name}</div>
                              <div className="text-xs text-muted-foreground truncate">
                                {item?.description}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Active Indicator */}
                        {isActivePath(item?.path) && (
                          <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 ml-2" />
                        )}
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </nav>

          {/* Sidebar Footer */}
          <div className="border-t border-border p-4">
            {!isCollapsed ? (
              <div className="space-y-3">
                {/* Portfolio Summary */}
                <div className="bg-muted/30 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-muted-foreground">Portfolio Value</span>
                    <Icon name="TrendingUp" size={12} className="text-success" />
                  </div>
                  <div className="text-lg font-bold text-card-foreground">$24,750</div>
                  <div className="text-xs text-success">+12.5% this month</div>
                </div>

                {/* Quick Actions */}
                <div className="flex space-x-2">
                  <button className="flex-1 flex items-center justify-center space-x-2 py-2.5 px-3 bg-primary/10 text-primary rounded-lg text-xs font-medium hover:bg-primary/20 transition-colors duration-200 min-h-[44px]">
                    <Icon name="Plus" size={14} />
                    <span>Add</span>
                  </button>
                  <button className="flex-1 flex items-center justify-center space-x-2 py-2.5 px-3 bg-muted/50 text-muted-foreground rounded-lg text-xs font-medium hover:bg-muted transition-colors duration-200 min-h-[44px]">
                    <Icon name="BarChart3" size={14} />
                    <span>Analyze</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <button className="w-full p-2.5 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors duration-200 premium-hover min-h-[44px] flex items-center justify-center">
                  <Icon name="Plus" size={16} />
                </button>
                <button className="w-full p-2.5 bg-muted/50 text-muted-foreground rounded-lg hover:bg-muted transition-colors duration-200 premium-hover min-h-[44px] flex items-center justify-center">
                  <Icon name="BarChart3" size={16} />
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>
      {/* Mobile Sidebar Toggle Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed top-[4.5rem] left-3 z-40 lg:hidden p-3 bg-card border border-border rounded-lg shadow-premium premium-hover min-w-[44px] min-h-[44px] flex items-center justify-center"
        aria-label="Open navigation"
      >
        <Icon name="Menu" size={20} className="text-card-foreground" />
      </button>
    </>
  );
};

export default Sidebar;